/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

/**
 * Find the ImageGroup that matches the criteria supplied
 *
 * @param {Object} imageGroups - The product/variations image groups you want to search.
 * @param {Object} options - Search criteria to match on the ImageGroup object.
 * @param {string} options.viewType - Typically this refers to image sizes like small, medium, large. But can vary based on your back-end configuration.
 * @param {Object} options.selectedVariationAttributes - A key/value object consisting of attibute id's and their values.
 * @returns {Object} - The ImageGroup matching the search criteria
 */
export const findImageGroupBy = (imageGroups = [], options) => {
    let {viewType, selectedVariationAttributes = {}} = options

    const filteredByViewType = imageGroups.filter(
        ({viewType: imageGroupViewType}) => imageGroupViewType === viewType,
    )

    let result = findGroupWithAttributes(filteredByViewType, selectedVariationAttributes)

    if (!result || !result.images || result.images.length === 0) {
        const subModelAttributes = {...selectedVariationAttributes}
        delete subModelAttributes.skuIdentifier
        result = findGroupWithAttributes(filteredByViewType, subModelAttributes)
    }

    return result
}

const findGroupWithAttributes = (imageGroups, attributes) => {
    const refinableAttributeIds = [
        ...new Set(
            imageGroups
                .reduce((acc, {variationAttributes = []}) => [...acc, ...variationAttributes], [])
                .map(({id}) => id),
        ),
    ]

    const filteredAttributes = Object.keys(attributes).reduce((acc, curr) => {
        return refinableAttributeIds.includes(curr)
            ? {
                  ...acc,
                  [`${curr}`]: attributes[curr],
              }
            : acc
    }, {})

    return imageGroups.find(({variationAttributes = []}) => {
        const selectedIds = Object.keys(filteredAttributes)
        return selectedIds.every((selectedId) => {
            const selectedValue = filteredAttributes[selectedId]
            return variationAttributes.find(
                ({id, values}) =>
                    id === selectedId && values.every(({value}) => value === selectedValue),
            )
        })
    })
}
